import React, { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Box, Typography, FormControl, Select, Option } from "@mui/joy";

import { supabase } from "../../supabaseClient";

import { useToast } from "../../context/ToastContext";
import { useModal } from "../../context/ModalContext";

import { Student } from "../../types";
import { RelationshipType, RelationshipTypeLabels } from "../../types/ParentGuardian";
import { formatStudentName } from "../../utils/names";

const relationshipOptions = Object.entries(RelationshipTypeLabels).map(([value, label]) => ({
  value,
  label,
}));

// Validation schema for parent/guardian assignment
const addParentGuardianSchema = yup.object().shape({
  parent_guardian_id: yup.string().required("Parent / Guardian selection is required"),
  relationship: yup
    .mixed<RelationshipType>() // Ensure this matches the enum type
    .oneOf(Object.values(RelationshipType), "Invalid relationship")
    .required("Relationship is required"),
});

interface AddParentGuardianFormValues {
  student_id: string;
  parent_guardian_id: string;
  relationship: RelationshipType;
}

interface AddParentGuardianToStudentModalProps {
  student: Student;
  onAssignmentUpdated?: () => void;
}

const AddParentGuardianToStudentModal: React.FC<AddParentGuardianToStudentModalProps> = ({
  student,
  onAssignmentUpdated,
}) => {
  const { closeModal, setModalTitle, registerSubmitCallback } = useModal();
  const { showToast } = useToast();
  const [parentGuardians, setParentGuardians] = useState<any[]>([]);
  const [assignedParentGuardians, setAssignedParentGuardians] = useState<string[]>([]);
  const [filteredParentGuardians, setFilteredParentGuardians] = useState<any[]>([]);

  const defaultValues: AddParentGuardianFormValues = {
    student_id: student.id,
    parent_guardian_id: "",
    relationship: RelationshipType.Parent,
  };

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<AddParentGuardianFormValues>({
    resolver: yupResolver(addParentGuardianSchema) as any, // Explicit cast to resolve internal type inference issue
    defaultValues,
    mode: "onTouched",
  });

  const fetchParentGuardians = async () => {
    try {
      const { data: guardians, error: fetchGuardiansError } = await supabase
        .from("parent_guardians")
        .select("*");

      if (fetchGuardiansError) throw fetchGuardiansError;

      const { data: assigned, error: fetchAssignedError } = await supabase
        .from("students_parent_guardians")
        .select("parent_guardian_id")
        .eq("student_id", student.id);

      if (fetchAssignedError) throw fetchAssignedError;

      setParentGuardians(guardians || []);
      setAssignedParentGuardians((assigned || []).map((entry) => entry.parent_guardian_id));
    } catch (error) {
      console.error("Error fetching parent/guardian data:", error);
      showToast("Failed to load parent/guardian data.", "danger", "solid");
    }
  };

  // Fetch parent/guardian data
  useEffect(() => {
    fetchParentGuardians();
    setModalTitle(`Add Parent / Guardian to ${formatStudentName(student)}`);
    registerSubmitCallback(async () => await handleSubmit(onSubmit)());
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [student.first_name, student.last_name, student.preferred_first_name, registerSubmitCallback]);

  // Filter parent/guardians by last name or search input
  const filterGuardians = useCallback(
    (searchValue: string) => {
      const lowercasedSearch = searchValue.toLowerCase();

      const filtered = parentGuardians
        .sort((a, b) => {
          if (a.last_name === student.last_name && b.last_name !== student.last_name) return -1;
          if (a.last_name !== student.last_name && b.last_name === student.last_name) return 1;
          return 0;
        })
        .filter((guardian) => {
          const isMatch =
            guardian.first_name.toLowerCase().includes(lowercasedSearch) ||
            guardian.last_name.toLowerCase().includes(lowercasedSearch);
          return isMatch;
        });

      setFilteredParentGuardians(filtered);
    },
    [parentGuardians, student.last_name]
  );

  useEffect(() => {
    filterGuardians("");
  }, [parentGuardians, filterGuardians]);

  const onSubmit = useCallback(
    async (formData: AddParentGuardianFormValues) => {
      try {
        if (assignedParentGuardians.includes(formData.parent_guardian_id)) {
          showToast("This parent / guardian is already assigned to the student.", "danger", "solid");
          return;
        }

        const { error } = await supabase
          .from("students_parent_guardians")
          .insert({
            student_id: student.id,
            parent_guardian_id: formData.parent_guardian_id,
            relationship: formData.relationship,
          });

        if (error) throw error;

        showToast("Parent / Guardian added successfully.", "success", "solid");

        if (onAssignmentUpdated) {
          onAssignmentUpdated();
        }

        closeModal();
      } catch (error) {
        console.error("Error assigning parent / guardian:", error);
        showToast("Error assigning parent / guardian.", "danger", "solid");
      }
    },
    [assignedParentGuardians, student.id, showToast, onAssignmentUpdated, closeModal]
  );

  return (
    <Box sx={{ height: "325px", position: "relative" }}>
      <form>
        <Box display="flex" flexDirection="column" gap={2} sx={{ width: "100%" }}>
          {/* Parent/Guardian Selection */}
          <FormControl sx={{ flex: 1, minWidth: "100%" }}>
            <Typography component="label" htmlFor="parent_guardian_id">
              Parent / Guardian
            </Typography>
            <Controller
              name="parent_guardian_id"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  id="parent_guardian_id"
                  value={field.value || ""}
                  onChange={(event, newValue) => {
                    field.onChange(newValue || ""); // Ensure value is a string
                    setValue("parent_guardian_id", newValue || ""); // Safely handle null
                  }}
                  placeholder="Select a Parent / Guardian..."
                  sx={{ height: "74px" }}
                >
                  {filteredParentGuardians.map((guardian) => {
                    const isAssigned = assignedParentGuardians.includes(guardian.id);

                    return (
                      <Option
                        key={guardian.id}
                        value={guardian.id}
                        disabled={isAssigned}
                        sx={{
                          color: isAssigned ? "text.disabled" : "text.primary",
                          "& .MuiTypography-root": {
                            opacity: isAssigned ? 0.6 : 1,
                          },
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" width="100%">
                          <Typography>
                            {guardian.first_name} {guardian.last_name}
                            {isAssigned ? " (Already Added)" : ""}
                          </Typography>
                        </Box>
                      </Option>
                    );
                  })}
                </Select>
              )}
            />
            <Box sx={{ minHeight: "26px" }}>
              {errors.parent_guardian_id && (
                <Typography sx={{ color: "red", mt: 1 }}>{errors.parent_guardian_id.message}</Typography>
              )}
            </Box>
          </FormControl>

          {/* Relationship Selection */}
          <FormControl sx={{ flex: 1, minWidth: "100%", mt: 2 }}>
            <Typography component="label" htmlFor="relationship">
              Relationship to Student
            </Typography>
            <Controller
              name="relationship"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  id="relationship"
                  value={field.value || ""}
                  onChange={(event, newValue) => field.onChange(newValue)}
                  placeholder="Select Relationship"
                  sx={{ height: "74px" }}
                >
                  {relationshipOptions.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              )}
            />
            <Box sx={{ minHeight: "26px" }}>
              {errors.relationship && (
                <Typography sx={{ color: "red", mt: 1 }}>{errors.relationship.message}</Typography>
              )}
            </Box>
          </FormControl>
        </Box>
      </form>
    </Box>
  );
}

export default AddParentGuardianToStudentModal;